<template>
  <div class="secondary-links">
    <Heading class="secondary-links__title" :level="3">{{ title }}</Heading>
    <div class="secondary-links__container" data-testid="secondary-links-list">
      <div
        v-for="module in getModules"
        :key="module.id"
        class="secondary-links__divider"
      >
        <Row
          class="secondary-links__row"
          role="button"
          tabindex="0"
          chevron
          has-interaction
          :has-line="false"
          :left-title="module.name"
          :data-testid="module.id"
          @click="openModule(module)"
          @keydown.space.enter.prevent="openModule(module)"
        >
          <template #icon-left>
            <InitialAvatarIcon
              class="secondary-links__icon"
              :icon="module.icon"
              size="medium"
            />
          </template>
        </Row>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  BaseIcon,
  Heading,
  Row,
  InitialAvatarIcon
} from '@warrenbrasil/nebraska-web';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';

export enum ModuleType {
  EXTERNAL,
  INTERNAL,
  LANDING_PAGE
}

export interface Module {
  type: ModuleType;
  icon: string;
  name: string;
  path: string;
  feature?: FeatureFlags;
  id: string;
  trackFunction?: () => void;
  shouldRender?: boolean;
}

const customerModule = namespace('CustomerModule');

@Component({
  components: {
    BaseIcon,
    Heading,
    Row,
    InitialAvatarIcon
  }
})
export default class HomeSecondaryLinks extends Vue {
  readonly NebraskaColors = NebraskaColors;

  @Prop({ type: String, required: true })
  private title?: string;

  @Prop({ type: Array, required: true })
  private modules!: Module[];

  @customerModule.State('isImpersonated')
  readonly isImpersonated!: boolean;

  @customerModule.Getter('isCustomerPJ')
  readonly isCustomerPJ!: boolean;

  public get getModules() {
    return this.modules.filter(module => {
      let isFeatureEnabled = true;
      let shouldRender = true;

      if (module.feature)
        isFeatureEnabled = FeatureFlagsModule.isEnabled(module.feature);
      if (typeof module.shouldRender === 'boolean')
        shouldRender = module.shouldRender;

      return isFeatureEnabled && shouldRender && module;
    });
  }

  public openModule(module: Module) {
    if (module.trackFunction) module.trackFunction();

    if (module.type === ModuleType.INTERNAL) {
      this.$router.push(module.path);
    } else if (module.type === ModuleType.LANDING_PAGE) {
      window.open(module.path);
    } else {
      window.location.assign(module.path);
    }
  }
}
</script>

<style lang="less" scoped>
.secondary-links {
  &__title {
    margin-bottom: @size-spacing-x400;
  }

  &__divider {
    &:first-child {
      border-top: 0;
    }
    border-top: 1px solid @divider-primary;
  }

  &__row {
    width: auto;
    display: flex;
    align-items: center;

    /deep/ .container-left {
      .text-p-3 {
        .text-p-4();
      }
    }

    /deep/ .icon:last-of-type {
      margin: 0px 0px 0px @size-spacing-x350;
    }
  }

  &__icon {
    color: @element-primary;
    background-color: @background-tertiary;
  }
}
</style>
